import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Grid,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import starburst_add_privilege_button from "./images/starburst_add_privilege_button.png";
import starburst_add_role from "./images/starburst_add_role.png";
import starburst_add_role_form from "./images/starburst_add_role_form.png";
import starburst_api_auth_token from "./images/starburst_api_auth_token.png";
import starburst_assign_user_button from "./images/starburst_assign_user_button.png";
import starburst_generate_api_token from "./images/starburst_generate_new_token.png";
import starburst_privileges_summary from "./images/starburst_privileges_summary.png";
import starburst_roles_and_privileges from "./images/starburst_roles_and_privileges.png";
import starburst_select_role_for_token from "./images/starburst_select_role_for_token.png";
import starburst_token_details from "./images/starburst_token_details.png";
import starburst_users_assigned_to_role from "./images/starburst_users_assigned_to_role.png";
import StarburstConnectForm from "./StarburstConnectForm";

const StarburstIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const colors = useColors();
  const [loading, setLoading] = useState(false);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("starburst");
  const queryClient = useQueryClient();

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  const { registerStarburstIntegration } = useAPI();

  useEffect(() => {
    setLoading(isIntegratedLoading);
  }, [isIntegratedLoading]);

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      await registerStarburstIntegration(
        values.client_id,
        values.client_secret,
        values.account_url
      );
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success("Starburst integration successful");
    } catch (error) {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    } finally {
      setLoading(false);
    }
  };

  const starburstRequiredPrivileges = [
    "Account -> Manage Security",
    "Account -> View all query history",
    "Account -> View audit log",
    "Cluster -> Choose cluster (Should be a minimal cluster) -> Monitor cluster",
    "Data -> Select galaxy_telemetry -> Select from table",
  ];

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !loading
            ? "Once integrated, Breeze will connect to your Starburst instance to collect data catalogs and correlate security gaps with your business assets."
            : ""
        }
        image={logos.StarburstLogo}
        iconBorderRadius={0}
        name={"Starburst"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Starburst is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Access your Starburst console">
                  <Typography variant="body2">
                    Make sure you have admin access to your Starburst instance -
                    required for creating API auth token.
                  </Typography>
                  <PrimaryButton
                    onClick={() => {
                      window.open(`https://galaxy.starburst.io/`, "_blank");
                    }}
                    style={{ textTransform: "none", width: "300px" }}
                  >
                    Access Starburst Galaxy
                  </PrimaryButton>
                  <Typography variant="body2">
                    Ensure you are logged in with an account that has
                    administrative privileges.
                  </Typography>
                </GuidelineStep>

                <GuidelineStep title="Create Role">
                  <Typography variant="body2">
                    In your Starburst console, navigate to{" "}
                    <b>'Access' &gt; 'Roles and Privileges'</b> on the sidebar.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: starburst_roles_and_privileges,
                        altext: "Starburst Roles and Privileges",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Click on the <b>'Add Role'</b> button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: starburst_add_role,
                        altext: "Starburst Add Role Form",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Create a new role specifically for the Breeze integration.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: starburst_add_role_form,
                        altext: "Starburst Add Role Form",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Click on the Role Name and navigate to the <b>'Users'</b>{" "}
                    tab.
                  </Typography>
                  <Typography variant="body2">
                    Click on the <b>'Assign User'</b> button and assign the role
                    to the current user.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: starburst_assign_user_button,
                        altext: "Starburst Assign User",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Make sure you see the user assigned to the role in the
                    table, For example:
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: starburst_users_assigned_to_role,
                        altext: "Starburst Users Assigned to Role",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Navigate to the <b>'Privileges'</b> tab and click on{" "}
                    <b>'Add Privilege'</b> button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: starburst_add_privilege_button,
                        altext: "Starburst Add Privilege Button",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Add the following privileges:
                  </Typography>
                  <Accordion
                    sx={{
                      width: "700px",
                      backgroundColor: colors.grey_200,
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography
                        variant="subtitle2"
                        color={colors.primary_darker}
                      >
                        The list of required privileges
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {starburstRequiredPrivileges.map((privilege, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Checkbox />
                          <Typography>{privilege}</Typography>
                        </div>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                  <Typography variant="body2">
                    The <b>'Privileges added'</b> section should look like this:
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: starburst_privileges_summary,
                        altext: "Starburst Role Privileges",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Click on the <b>'Save Privileges'</b> button.
                  </Typography>
                </GuidelineStep>

                <GuidelineStep title="Create API Auth Token">
                  <Typography variant="body2">
                    Go to <b>'Access' &gt; 'API auth token'</b> on the sidebar.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: starburst_api_auth_token,
                        altext: "Starburst API auth token",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Generate a new API token
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: starburst_generate_api_token,
                        altext: "Starburst Generate API Token",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Select the role you created in the previous step.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: starburst_select_role_for_token,
                        altext: "Starburst Select Role for Token",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Click on the <b>'Create Token'</b> button.
                  </Typography>
                  <Typography variant="body2">
                    Copy the Client ID and Client Secret Key and fill in the
                    form below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: starburst_token_details,
                        altext: "Starburst Token Details",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    <strong>NOTE:</strong> You won't be able to see the Client
                    Secret Key again, so make sure to fill the form before
                    closing the window.
                  </Typography>
                  <Typography variant="body2">
                    Mark the checkbox and click on the <b>'Done'</b>.
                  </Typography>
                </GuidelineStep>

                <GuidelineStep title="Your Account URL">
                  <Typography variant="body2">
                    Your Starburst account URL is typically in the format:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ ml: 2, fontFamily: "monospace" }}
                  >
                    https://your-account.galaxy.starburst.io
                  </Typography>
                  <Typography variant="body2">
                    You can find this in your browser's address bar when logged
                    into Starburst.
                  </Typography>
                </GuidelineStep>

                <GuidelineStep title="Configure Integration">
                  <Typography variant="body2">
                    Enter the API auth token credentials and account URL in the
                    form below to complete the integration.
                  </Typography>
                  <StarburstConnectForm
                    onSubmit={onSubmit}
                    guideMode={guideMode}
                  />
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default StarburstIntegration;
